import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Native `<select>` element styled to match Input.
 *
 * Adds a custom chevron via background-image since we strip the
 * native arrow with `appearance-none`. The chevron uses `currentColor`
 * via an inline SVG data-URI so it inherits the text color and works
 * in both light and dark themes.
 */
export const selectVariants = cva(
  // Base
  'block w-full rounded-lg border bg-ink-50 text-ink-900 ' +
    'appearance-none bg-no-repeat ' +
    'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 ' +
    'disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      variant: {
        default:
          'border-ink-200 hover:border-ink-300 focus-visible:ring-accent-500/40 focus-visible:border-accent-500',
        error:
          'border-danger-500 focus-visible:ring-danger-500/40 focus-visible:border-danger-500',
      },
      size: {
        sm: 'h-8 pl-2.5 pr-8 text-sm bg-[length:14px] bg-[position:right_0.5rem_center]',
        md: 'h-10 pl-3 pr-9 text-sm bg-[length:16px] bg-[position:right_0.625rem_center]',
        lg: 'h-12 pl-4 pr-10 text-base bg-[length:18px] bg-[position:right_0.75rem_center]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

// Chevron-down icon as an inline SVG data URI. Uses currentColor so it
// follows the select's text color (which is theme-aware).
const CHEVRON_SVG =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")";

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>,
    VariantProps<typeof selectVariants> {}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, variant, size, style, children, ...props }, ref) => {
    return (
      <select
        ref={ref}
        className={cn(selectVariants({ variant, size }), className)}
        style={{ backgroundImage: CHEVRON_SVG, ...style }}
        {...props}
      >
        {children}
      </select>
    );
  }
);
Select.displayName = 'Select';
